import { useEffect, useState } from "react";
import { CheckCircle2, Circle, Upload } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import { isViewable } from "../../shared/files";
import type { EvidenceView, WorkItemDetail, WorkItemEvidenceKind } from "../../shared/types/api";
import {
  Button,
  buttonClasses,
  cn,
  Card,
  CardBody,
  CardHeader,
  Field,
  InlineAlert,
  Input,
  Spinner,
  Textarea,
} from "../../shared/ui";
import { EVIDENCE_LABELS, TYPE_EVIDENCE, outstandingEvidence } from "./workItemVocabulary";

/**
 * What a non-development work item still has to produce before it can be completed (§15).
 *
 * <p>Renders nothing for the six development types: {@link TYPE_EVIDENCE} lists them as owing
 * nothing, so `required` is empty here. The API is what actually enforces this — refusing to
 * complete an analysis without its document, a demo preparation without its screenshot, or a
 * migration without both its queries and a screenshot — this panel exists so nobody discovers
 * that refusal only after pressing Complete.
 *
 * <p>A migration's queries are kept apart from the file-based kinds throughout: they are typed
 * text on {@link WorkItemDetail.migrationDetail}, saved through its own endpoint, not a file
 * attached through the evidence endpoint (which refuses `kind=QUERIES` for exactly that reason).
 */
export function WorkItemEvidencePanel({
  item,
  onChanged,
}: {
  item: WorkItemDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const manage = can("WORK_ITEM_EDIT");

  const [uploadingKind, setUploadingKind] = useState<WorkItemEvidenceKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [queries, setQueries] = useState(item.migrationDetail?.queries ?? "");
  const [databaseName, setDatabaseName] = useState(item.migrationDetail?.databaseName ?? "");
  const [savingQueries, setSavingQueries] = useState(false);

  // Resynced on the item's id, not only at mount: the route renders one element, so navigating
  // from one migration to another changes this prop without remounting, and the useState
  // initialisers above would otherwise go on holding the previous item's queries.
  useEffect(() => {
    setQueries(item.migrationDetail?.queries ?? "");
    setDatabaseName(item.migrationDetail?.databaseName ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const required = TYPE_EVIDENCE[item.workItemType];

  if (required.length === 0) {
    return null;
  }

  const outstanding = outstandingEvidence(item);

  const onFileChosen = async (
    kind: Exclude<WorkItemEvidenceKind, "QUERIES">,
    file: File | undefined,
  ) => {
    if (!file) {
      return;
    }
    setError(null);
    setUploadingKind(kind);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("kind", kind);
      await api.upload(`/api/v1/work-items/${item.id}/evidence`, body);
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not attach that file.");
    } finally {
      setUploadingKind(null);
    }
  };

  /* Nothing may be awaited before api.view: it opens its tab while the browser still counts this
     as the click that asked for it, and a tab opened any later is a blocked popup. */
  const openEvidence = async (row: EvidenceView) => {
    if (!row.attachmentId) {
      return;
    }
    setError(null);
    try {
      await api.view(`/api/v1/attachments/${row.attachmentId}/content`, row.fileName ?? "file");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not open that file.");
    }
  };

  const saveQueries = async () => {
    setError(null);
    setSavingQueries(true);
    try {
      await api.put(`/api/v1/work-items/${item.id}/migration-detail`, {
        queries,
        databaseName: databaseName || null,
      });
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save the queries.");
    } finally {
      setSavingQueries(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Evidence"
        subtitle={
          outstanding.length === 0
            ? "Everything this work has to produce is here."
            : `Still needed before this can be completed: ${outstanding
                .map((kind) => EVIDENCE_LABELS[kind].toLowerCase())
                .join(", ")}.`
        }
      />
      <CardBody className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {required.includes("QUERIES") && (
          <div className="space-y-2 border-b border-ink-100 pb-4 last:border-0 last:pb-0">
            <Field
              label={EVIDENCE_LABELS.QUERIES}
              required
              hint="Kept verbatim — this is what an auditor reads and the next person reverses."
            >
              <Textarea
                rows={6}
                value={queries}
                disabled={!manage}
                onChange={(event) => setQueries(event.target.value)}
              />
            </Field>
            <Field label="Database" hint="Which database these queries ran against.">
              <Input
                type="text"
                value={databaseName}
                disabled={!manage}
                onChange={(event) => setDatabaseName(event.target.value)}
              />
            </Field>
            {manage && (
              <Button
                size="sm"
                loading={savingQueries}
                disabled={!queries.trim()}
                onClick={() => void saveQueries()}
              >
                Save queries
              </Button>
            )}
          </div>
        )}

        {required
          .filter((kind): kind is Exclude<WorkItemEvidenceKind, "QUERIES"> => kind !== "QUERIES")
          .map((kind) => (
            <EvidenceRow
              key={kind}
              kind={kind}
              /* Rows whose file has been deleted are dropped rather than shown greyed out:
                 they no longer satisfy anything, and a tick beside a name the server does not
                 count is the disagreement this panel exists to prevent. */
              held={item.evidence.filter(
                (row) => row.kind === kind && row.attachmentId != null,
              )}
              canManage={manage}
              uploading={uploadingKind === kind}
              onChoose={(file) => void onFileChosen(kind, file)}
              onOpen={(row) => void openEvidence(row)}
            />
          ))}
      </CardBody>
    </Card>
  );
}

function EvidenceRow({
  kind,
  held,
  canManage,
  uploading,
  onChoose,
  onOpen,
}: {
  kind: Exclude<WorkItemEvidenceKind, "QUERIES">;
  held: EvidenceView[];
  canManage: boolean;
  uploading: boolean;
  onChoose: (file: File | undefined) => void;
  onOpen: (row: EvidenceView) => void;
}) {
  const [dropTarget, setDropTarget] = useState(false);
  const droppable = canManage && !uploading;

  return (
    /*
     * The row takes a dropped file as well as a chosen one. Not a flourish: opening a file
     * picker is the browser's to grant, and a browser that declines — through a policy, an
     * extension, or a setting nobody here can see — leaves the button pressable and useless.
     * A drop never asks the browser for a picker, so it is the path that still works when the
     * other one silently does not.
     */
    <div
      className={cn(
        "flex items-start justify-between gap-3 rounded-lg transition-colors",
        dropTarget && "bg-brand-50 ring-2 ring-brand-400 ring-offset-2",
      )}
      onDragOver={(event) => {
        if (droppable) {
          // Without this the browser opens the file instead of letting the page have it.
          event.preventDefault();
          setDropTarget(true);
        }
      }}
      onDragLeave={() => setDropTarget(false)}
      onDrop={(event) => {
        if (!droppable) {
          return;
        }
        event.preventDefault();
        setDropTarget(false);
        onChoose(event.dataTransfer.files?.[0]);
      }}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {held.length > 0 ? (
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
          ) : (
            <Circle className="h-4 w-4 shrink-0 text-ink-300" />
          )}
          <span className="text-sm font-medium text-ink-900">{EVIDENCE_LABELS[kind]}</span>
        </div>
        {held.length > 0 ? (
          <ul className="ml-6 mt-1 space-y-0.5">
            {held.map((row) => (
              <li key={row.id} className="truncate text-xs text-ink-500">
                {row.attachmentId && isViewable(row.fileName) ? (
                  <button
                    type="button"
                    title="Open in a new tab"
                    className="text-brand-700 hover:underline"
                    onClick={() => onOpen(row)}
                  >
                    {row.fileName}
                  </button>
                ) : (
                  row.fileName
                )}
                {/* Who produced it, beside what it is. "What did we actually change in
                    production" is half a filename and half a person (§15). */}
                {row.attachedByName && (
                  <span className="text-ink-400"> · {row.attachedByName}</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="ml-6 mt-0.5 text-xs text-ink-400">
            Not attached yet.
            {canManage && " Drop a file here, or use Attach."}
          </p>
        )}
      </div>
      {canManage && (
        /* A real label around a real file input, rather than a button that calls click() on a
           hidden one. The scripted version depends on the browser honouring a synthetic click
           on a `display: none` input, which is exactly the part that silently does nothing when
           it is not honoured — and it leaves nothing on screen to press when it fails. A label
           needs no script to open the picker, so there is no such failure to have. */
        <label
          className={buttonClasses(
            "ghost",
            "sm",
            uploading ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          )}
        >
          {uploading ? <Spinner className="h-4 w-4 text-current" /> : <Upload className="h-4 w-4" />}
          Attach
          <input
            type="file"
            className="sr-only"
            disabled={uploading}
            onChange={(event) => {
              onChoose(event.target.files?.[0]);
              // Cleared so that picking the same file again still fires a change event.
              event.target.value = "";
            }}
          />
        </label>
      )}
    </div>
  );
}
